import React from "react";
import { useHistory } from "react-router-dom";

import { OrderDocuments } from "features/BranchDetails/components/OrderDocuments";
import {
    SERVICE_ORDER_STATUS_LABELS,
    SERVICE_ORDER_STATUS_TONES,
    SERVICE_ORDER_TYPE_LABELS,
} from "features/BranchDetails/constants";
import { StatusBadge } from "features/BranchDetails/styles";
import { formatPercent } from "features/Partners/percent";
import { InvoiceServiceOrderDto, PaymentDto, ServiceOrderStatus } from "services/serviceOrders/restModels";
import { formatDate } from "shared/lib/dateTime";
import { formatPrice } from "shared/lib/price";
import { DataTable, DataTableColumn } from "shared/ui/DataTable";

import { MutedLine } from "../styles";

interface PaymentsTableProps {
    items: PaymentDto[];
    busyInvoiceId: string | null;
    onOpenInvoice: (invoice: InvoiceServiceOrderDto) => void;
    onOpenAct: (invoice: InvoiceServiceOrderDto) => void;
    footer: React.ReactNode;
}

export const PaymentsTable = ({
    items,
    busyInvoiceId,
    onOpenInvoice,
    onOpenAct,
    footer,
}: PaymentsTableProps) => {
    const history = useHistory();

    const columns: DataTableColumn<PaymentDto>[] = [
        {
            key: "branch",
            title: "Заведение",
            role: "primary",
            width: 240,
            render: ({ organizationBranchId, organizationBranchName }) => (
                <>
                    <div>{organizationBranchName || "—"}</div>
                    <MutedLine>ID {organizationBranchId}</MutedLine>
                </>
            ),
        },
        {
            key: "status",
            title: "Статус",
            role: "secondary",
            width: 100,
            render: ({ order }) => {
                const status = order.status as ServiceOrderStatus;

                return (
                    <StatusBadge $tone={SERVICE_ORDER_STATUS_TONES[status]}>
                        {SERVICE_ORDER_STATUS_LABELS[status]}
                    </StatusBadge>
                );
            },
        },
        {
            key: "tariff",
            title: "Тариф",
            role: "secondary",
            grow: true,
            render: ({ order }) => (
                <>
                    <div>{`${order.tariffTitle} · ${order.periodTitle}`}</div>
                    <MutedLine>{SERVICE_ORDER_TYPE_LABELS[order.type]}</MutedLine>
                </>
            ),
        },
        {
            key: "price",
            title: "Сумма",
            role: "secondary",
            width: 110,
            render: ({ order }) => formatPrice(order.price.price, order.price.currencyId),
        },
        {
            key: "commission",
            title: "Вознаграждение",
            role: "secondary",
            width: 130,
            render: ({ order, commissionAmount, commissionPercent }) =>
                commissionAmount === null ? (
                    "—"
                ) : (
                    <>
                        <div>{formatPrice(commissionAmount, order.price.currencyId)}</div>
                        <MutedLine>{formatPercent(commissionPercent)}</MutedLine>
                    </>
                ),
        },
        {
            key: "date",
            title: "Дата",
            role: "secondary",
            width: 100,
            render: ({ order }) => formatDate(order.paidAt || order.createdAt),
        },
        {
            key: "document",
            title: "Документы",
            role: "secondary",
            width: 90,
            render: ({ order }) => (
                <OrderDocuments
                    order={order}
                    busy={order.type === "Invoice" && busyInvoiceId === order.invoiceId}
                    onOpenInvoice={onOpenInvoice}
                    onOpenAct={onOpenAct}
                />
            ),
        },
    ];

    return (
        <DataTable
            columns={columns}
            rows={items}
            rowKey={({ order }) => (order.type === "Card" ? `card-${order.orderId}` : order.invoiceId)}
            onRowClick={({ organizationBranchId }) =>
                history.push(`/branchesRegister/general/${organizationBranchId}`)
            }
            footer={footer}
        />
    );
};
